/**
 * Export card on /hr/leave-history-import — the inverse of the import wizard.
 *
 * Downloads leave records as a CSV in the exact layout the importer reads, so
 * HR can load them back into the legacy HRIS (or re-import here unchanged).
 * Date range pre-fills to the current year (1 Jan → today, clamped to 31 Dec).
 */

import { useState } from 'react'
import { Card } from '../../components/primitives/Card'
import { primaryBtn } from '../hr-leave-import/LeaveImportSteps'
import { exportLeaveHistory } from '../../api/hr-leave-history'
import { ApiError } from '../../api/client'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
] as const

function defaultRange(): { from: string; to: string } {
  const now = new Date()
  const year = now.getFullYear()
  const today = now.toISOString().slice(0, 10)
  const yearEnd = `${year}-12-31`
  return { from: `${year}-01-01`, to: today < yearEnd ? today : yearEnd }
}

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-2)',
  marginBottom: 4,
}
const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 14,
  minHeight: 40,
}

export function LeaveHistoryExportCard(): JSX.Element {
  const initial = defaultRange()
  const [from, setFrom] = useState(initial.from)
  const [to, setTo] = useState(initial.to)
  const [status, setStatus] = useState<string>('all')
  const [includeEmail, setIncludeEmail] = useState(false)
  const [includeEmployeeReason, setIncludeEmployeeReason] = useState(false)
  const [includeApproverReason, setIncludeApproverReason] = useState(false)
  const [includeOverCapacity, setIncludeOverCapacity] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const invalidRange = to < from

  async function handleExport(): Promise<void> {
    setError(null)
    setBusy(true)
    try {
      await exportLeaveHistory({
        from,
        to,
        status,
        includeEmail,
        includeEmployeeReason,
        includeApproverReason,
        includeOverCapacity,
      })
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Export failed. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card padding={24}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)' }}>
          Pick a date range and export. The file uses the same columns as the import, so it loads
          straight back into the old system. Large exports come as a zip of files, each under the
          import limit.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
            alignItems: 'end',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="export-from" style={labelStyle}>
              From
            </label>
            <input
              id="export-from"
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              style={fieldStyle}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="export-to" style={labelStyle}>
              To
            </label>
            <input
              id="export-to"
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              style={fieldStyle}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <label htmlFor="export-status" style={labelStyle}>
              Status
            </label>
            <select
              id="export-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={fieldStyle}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <fieldset
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '12px 14px',
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <legend style={{ ...labelStyle, marginBottom: 0, padding: '0 4px' }}>
            Optional columns
          </legend>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeEmail}
              onChange={(e) => setIncludeEmail(e.target.checked)}
            />
            Email
            <span style={{ color: 'var(--text-3)' }}>— the employee's work email</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeEmployeeReason}
              onChange={(e) => setIncludeEmployeeReason(e.target.checked)}
            />
            Employee reason
            <span style={{ color: 'var(--text-3)' }}>— what the requester wrote</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeApproverReason}
              onChange={(e) => setIncludeApproverReason(e.target.checked)}
            />
            Approver reason
            <span style={{ color: 'var(--text-3)' }}>— the approval note or reject reason</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeOverCapacity}
              onChange={(e) => setIncludeOverCapacity(e.target.checked)}
            />
            Above capacity
            <span style={{ color: 'var(--text-3)' }}>— Yes when approved over the team cap</span>
          </label>
        </fieldset>

        {error != null ? (
          <div
            role="alert"
            style={{
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              border: '1px solid var(--danger-line)',
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        ) : null}

        <div>
          <button
            type="button"
            onClick={handleExport}
            disabled={busy || invalidRange}
            style={primaryBtn(busy || invalidRange)}
          >
            {busy ? 'Preparing…' : 'Export CSV'}
          </button>
        </div>
      </div>
    </Card>
  )
}
